/**
 * Contextual in-body links from service and industry pages to the single most
 * relevant blog post. Anchor text is a natural phrase inside the sentence, not
 * a "related articles" widget.
 */

export interface PostLink {
  to: string;
  before: string;
  anchor: string;
  after: string;
}

export const SERVICE_POST_LINKS: Record<string, PostLink> = {
  "google-ads": {
    to: "/blog/why-did-my-google-ads-conversions-stop",
    before: "Most accounts we inherit have a measurement problem before they have a bidding problem, which is why ",
    anchor: "Google Ads conversions stop reporting",
    after: " far more often than campaigns actually stop working.",
  },
  "meta-ads": {
    to: "/blog/meta-ad-strategies-for-pool-cleaning-vs-repair-leads",
    before: "Creative and offer have to match the job being sold, and the split between ",
    anchor: "Meta ad strategies for recurring service versus one-off repair leads",
    after: " is the clearest example of how differently the same audience responds.",
  },
  "local-services-ads": {
    to: "/blog/local-f-b-paid-media-integrating-lsa-meta-and-google-ads",
    before: "LSA works hardest when it is not run in isolation: ",
    anchor: "integrating LSA with Meta and Google Ads",
    after: " is what keeps verified calls from cannibalising the rest of the account.",
  },
  "youtube-ads": {
    to: "/blog/meta-vs-google-ads-dtc-budget-allocation-under-50k",
    before: "Video earns its place once the demand-capture channels are saturated, which is the same question behind ",
    anchor: "allocating a paid media budget under $50K across Meta and Google",
    after: ".",
  },
};

export const INDUSTRY_POST_LINKS: Record<string, PostLink> = {
  roofing: {
    to: "/blog/why-did-my-google-ads-conversions-stop",
    before: "Storm accounts are most exposed to broken tracking, so learn why ",
    anchor: "Google Ads conversions stop reporting",
    after: " before cutting a campaign still producing calls.",
  },
  painting: {
    to: "/blog/design-build-lead-gen-stop-bidding-for-low-margin-jobs",
    before: "Repaints and full exterior projects carry different margins, the same trap behind ",
    anchor: "bidding for low-margin jobs in design-build lead gen",
    after: ".",
  },
  "pool-service": {
    to: "/blog/meta-ad-strategies-for-pool-cleaning-vs-repair-leads",
    before: "Cleaning contracts and repairs need separate creative: ",
    anchor: "Meta ad strategies for pool cleaning versus repair leads",
    after: ".",
  },
  landscaping: {
    to: "/blog/hardscape-lead-qualification-improving-google-ads-show-rates",
    before: "Large installs depend on the estimate actually happening, so qualification and ",
    anchor: "improving Google Ads show rates on hardscape leads",
    after: " matter more than lead volume.",
  },
  "general-contractors": {
    to: "/blog/design-build-lead-gen-stop-bidding-for-low-margin-jobs",
    before: "Margin moves most when you ",
    anchor: "stop bidding for low-margin design-build jobs",
    after: " and fund the work you want.",
  },
  "hardscape-drywall": {
    to: "/blog/hardscape-lead-qualification-improving-google-ads-show-rates",
    before: "We start with the biggest leak, quote-stage drop-off: ",
    anchor: "hardscape lead qualification and Google Ads show rates",
    after: ".",
  },
};

/**
 * Additional in-body post links for pages that carry more than one relevant post.
 * Rendered as further prose sentences, not a related-posts list.
 */
export const SERVICE_EXTRA_POST_LINKS: Record<string, PostLink[]> = {
  "local-services-ads": [
    {
      to: "/blog/mastering-lsa-disputes-recover-20-of-monthly-ad-spend",
      before: "Google now credits low-quality LSA leads automatically, and ",
      anchor: "how Local Services Ads lead credits used to work",
      after: " covers the older process, with a note on how credits work now.",
    },
    {
      to: "/blog/boost-lsa-rank-the-review-acceleration-playbook-for-home-services",
      before: "Position in LSA is downstream of reviews and response time rather than bids, which is the whole argument of ",
      anchor: "the review acceleration playbook for boosting LSA rank",
      after: ".",
    },
  ],
  "meta-ads": [
    {
      to: "/blog/gym-membership-meta-ads-90-day-cac-and-creative-strategy",
      before: "Subscription offers behave differently again, and ",
      anchor: "90-day CAC and creative strategy for gym membership Meta ads",
      after: " is the clearest worked example of judging creative on retained members rather than signups.",
    },
    {
      to: "/blog/why-does-meta-report-more-purchases-than-my-store",
      before: "Once the pixel and the Conversions API are both live, the first thing to verify is deduplication: ",
      anchor: "why Meta reports more purchases than your store made",
      after: " explains what a missing event ID does to the numbers.",
    },
  ],
  "youtube-ads": [
    {
      to: "/blog/youtube-ad-formats-for-each-stage-of-the-buyer-journey",
      before: "Format choice is a funnel decision before it is a creative one, which we set out in ",
      anchor: "YouTube ad formats for each stage of the buyer journey",
      after: ".",
    },
  ],
};
